/**
 * Modal component using Radix UI
 */

import * as Dialog from '@radix-ui/react-dialog';
import {ReactNode} from 'react';
import {cn} from '../../utils/cn';

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = 'md',
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 dark:bg-black/70 z-40" />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'bg-white dark:bg-[#252526] rounded-lg shadow-xl z-50',
            'border border-gray-200 dark:border-gray-700',
            'focus:outline-none',
            {
              'w-full max-w-sm': size === 'sm',
              'w-full max-w-md': size === 'md',
              'w-full max-w-lg': size === 'lg',
              'w-full max-w-2xl': size === 'xl',
            }
          )}
        >
          {(title || description) && (
            <div className="px-6 pt-6 pb-4">
              {title && (
                <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-[#CCCCCC] mb-2">
                  {title}
                </Dialog.Title>
              )}
              {description && (
                <Dialog.Description className="text-sm text-gray-600 dark:text-[#858585]">
                  {description}
                </Dialog.Description>
              )}
            </div>
          )}
          <div className="px-6 pb-6">{children}</div>
          <Dialog.Close
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-[#858585] dark:hover:text-[#CCCCCC] transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

